import { useToast } from "@/components/ui/use-toast";
import { APIError, ValidationError } from "@/core/api/errors";
import { ApiError } from "@/services/api";
import { useTranslation } from "react-i18next";
import { getApiErrorDescription } from "@/lib/utils/getApiErrorDescription";
import { TFunction } from "i18next";
import {
  UseMutationOptions,
  UseMutationResult,
  useMutation,
} from "@tanstack/react-query";

function isLegacyApiError(error: unknown): error is ApiError {
  return (
    typeof error === "object" &&
    error !== null &&
    "errorCode" in error &&
    typeof (error as { errorCode?: unknown }).errorCode === "number"
  );
}

function getMutationErrorDescription(error: Error, t: TFunction) {
  if (isLegacyApiError(error)) {
    return getApiErrorDescription(error, t);
  }

  if (error instanceof ValidationError) {
    return error.message || t("errors.unknownError");
  }

  if (error instanceof APIError) {
    return error.message || t("errors.unknownError");
  }

  if (error.message && error.message.trim().length > 0) {
    return error.message;
  }

  return t("errors.unknownError");
}

export function useMutationWithErrorHandling<
  TData,
  TError extends Error,
  TVariables,
  TContext,
>(
  options?: UseMutationOptions<TData, TError, TVariables, TContext> & {
    errorTitle?: string;
  },
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { t } = useTranslation();
  const { toast } = useToast();
  const {
    errorTitle,
    onError: onErrorFromProps,
    ...restOptions
  } = options || {};
  const defaultErrorHandler = (error: TError) => {
    toast({
      title: errorTitle || t("errors.genericTitle"),
      description: getMutationErrorDescription(error, t),
      variant: "destructive",
    });
  };

  return useMutation<TData, TError, TVariables, TContext>({
    ...restOptions,
    onError: onErrorFromProps || defaultErrorHandler,
  });
}
